import { filterSidePanelEvent, useDebouncedCalendarRefreshEvent } from '@cmg/common';
import { Alert, AlertTitle, CircularProgress, Stack } from '@cmg/design-system';
import React from 'react';

import ErrorRenderer from '../../../common/components/errorRenderer/Error-Renderer';
import { OfferingProfile } from '../../offering-profile-mui/OfferingProfile';
import { useOfferingSidePanelOfferingId } from '../hooks/useOfferingSidePanelMatch';
import { isReg144A } from '../order-book/compliance/context/hooks/certificates/Certificates.model';
import { getComplianceWarningMessage } from './BuySideOfferingDetailsRoute.model';
import { OfferingDetailsLayout } from './components/OfferingDetailsLayout';
import { useOfferingDetails_PublishedOfferingQuery } from './graphql/__generated__';

function BuySideOfferingDetailsRoute() {
  const offeringId = useOfferingSidePanelOfferingId();

  const { data, loading, error, refetch } = useOfferingDetails_PublishedOfferingQuery({
    variables: { offeringId },
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });

  const filterFn = React.useMemo(() => {
    return filterSidePanelEvent(offeringId);
  }, [offeringId]);

  useDebouncedCalendarRefreshEvent({
    callback: React.useCallback(() => refetch(), [refetch]),
    filterFn,
  });

  const [offering] = data?.publishedOfferings?.items ?? [];

  const hasReg144A = isReg144A(offering?.instruments[0]);

  if (error) {
    if (
      error.graphQLErrors.some(
        // We assume that any AUTH_NOT_AUTHORIZED in the context of buyside refers to missing relationship with managers
        // this might be improved in future with check against the explicit code or subcode
        // e.g. gqlError.extensions?.subcode === 'BUYSIDE_NO_RELATIONSHIP_WITH_MANAGER'
        gqlError => gqlError.extensions?.code === 'AUTH_NOT_AUTHORIZED'
      )
    ) {
      return (
        <OfferingDetailsLayout>
          <Alert severity="warning">{getComplianceWarningMessage(hasReg144A)}</Alert>
        </OfferingDetailsLayout>
      );
    }

    return <ErrorRenderer error={error} />;
  }
  if (loading) {
    return (
      <Stack alignContent="center" alignItems="center">
        <CircularProgress size={20} />
      </Stack>
    );
  }

  if (!offering) {
    return (
      <OfferingDetailsLayout>
        <Alert severity="error">
          <AlertTitle>This Offering could not be found.</AlertTitle>
          This Offering ID is no longer valid. The offering may have been terminated, withdrawn, or
          deleted. Please <a href="mailto:support@cmgx.io">contact CMG support</a> for more
          information.
        </Alert>
      </OfferingDetailsLayout>
    );
  }
  return (
    <OfferingDetailsLayout>
      <OfferingProfile offering={offering} />
    </OfferingDetailsLayout>
  );
}

export default BuySideOfferingDetailsRoute;
